import { createContext, use, useContext, useState } from "react";
import { config } from "@/config/env";
import { AppConfigContext } from "@/contexts/AppConfigContext";
import { logError } from "@/utils/logError";

export type CookieBannerDisplay = "inline" | "popup" | "modal" | "hidden";

export const FEATURE_GROUPS = [
  {
    title: "Demo",
    flags: [
      {
        key: "demoMode" as const,
        label: "Demo mode (no real bookings)",
        defaultValue: config.demoMode,
      },
      {
        key: "cookieBanner" as const,
        label: "Cookie banner",
        defaultValue: "hidden" as CookieBannerDisplay,
        options: [
          { value: "inline" as const, label: "Inline" },
          { value: "popup" as const, label: "Popup" },
          { value: "modal" as const, label: "Modal" },
          { value: "hidden" as const, label: "Hidden" },
        ],
      },
    ],
  },
  {
    title: "Alternative dates",
    flags: [
      {
        key: "periodSelection" as const,
        label: "Period selection mode",
        defaultValue: "month-view-default" as const,
        options: [
          { value: "month-view-a" as const, label: "Month View A" },
          { value: "month-view-b" as const, label: "Month View B" },
          { value: "month-view-c" as const, label: "Month View C" },
          { value: "week-view" as const, label: "Week View" },
          { value: "airbnb" as const, label: "AirBnb style" },
          { value: "month-view-default" as const, label: "Month View Default" },
        ],
      },
      {
        key: "disabledStrikethrough" as const,
        label: "Strike through unavailable dates",
        defaultValue: true,
      },
      {
        key: "titleConfiguration" as const,
        label: "Title configuration",
        defaultValue: "text" as const,
        options: [
          { value: "text" as const, label: "Just text" },
          {
            value: "text_with_type_selector" as const,
            label: "Text + acco type selector",
          },
          {
            value: "text_with_option_selector" as const,
            label: "Text + acco option selector",
          },
        ],
      },
      {
        key: "showAccommodationOptionSelector" as const,
        label: "Show accommodation option selector",
        defaultValue: true,
      },
      {
        key: "nightsSelector" as const,
        label: "Nights selector control",
        defaultValue: "counter" as const,
        options: [
          { value: "both" as const, label: "Both" },
          { value: "counter" as const, label: "Counter only" },
          { value: "buttons" as const, label: "Buttons only" },
        ],
      },
    ],
  },
];

type FlagConfig = (typeof FEATURE_GROUPS)[number]["flags"][number];
type FlagValue<F extends FlagConfig> = F extends { options: readonly { value: infer V }[] }
  ? V
  : boolean;
export type FeatureFlags = { [F in FlagConfig as F["key"]]: FlagValue<F> };

const STORAGE_KEY = "campnerd_feature_flags";

function readFromStorage(): FeatureFlags {
  const allFlags = (FEATURE_GROUPS as { flags: FlagConfig[] }[]).flatMap((g) => g.flags);
  const flagDefaults = Object.fromEntries(
    allFlags.map((f) => [f.key, f.defaultValue]),
  ) as FeatureFlags;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const merged = { ...flagDefaults, ...(JSON.parse(stored) as Partial<FeatureFlags>) };
      // Drop stored values that are no longer valid options (e.g. after a rename)
      const mergedAny = merged as Record<string, unknown>;
      for (const flag of allFlags) {
        const options = "options" in flag ? (flag.options as { value: unknown }[]) : undefined;
        if (options && !options.some((o) => o.value === mergedAny[flag.key])) {
          mergedAny[flag.key] = flag.defaultValue;
        }
      }
      return merged;
    }
  } catch (error) {
    logError(error);
  }
  return flagDefaults;
}

const FeaturesContext = createContext<
  | {
      flags: FeatureFlags;
      setFlag: <K extends keyof FeatureFlags>(key: K, value: FeatureFlags[K]) => void;
      panelOpen: boolean;
      setPanelOpen: (open: boolean) => void;
    }
  | undefined
>(undefined);

export function FeaturesProvider({ children }: { children: React.ReactNode }) {
  const demoMode = use(AppConfigContext)?.demoMode;
  const [flags, setFlags] = useState<FeatureFlags>(() => {
    const stored = readFromStorage();
    if (typeof demoMode === "boolean") return { ...stored, demoMode };
    return stored;
  });
  const [panelOpen, setPanelOpen] = useState(false);

  function setFlag<K extends keyof FeatureFlags>(key: K, value: FeatureFlags[K]) {
    const next = { ...flags, [key]: value } as FeatureFlags;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (error) {
      logError(error);
    }
    setFlags(next);
  }

  return (
    <FeaturesContext.Provider value={{ flags, setFlag, panelOpen, setPanelOpen }}>
      {children}
    </FeaturesContext.Provider>
  );
}

export function useFeatureFlags() {
  const context = useContext(FeaturesContext);
  if (!context) {
    throw new Error("useFeatureFlags must be used within FeaturesProvider");
  }
  return context;
}
